import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import UncontrolledDefaultModal from 'components/Modal/UncontrolledDefaultModal';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import { Button, Row, Col } from 'reactstrap';

const ManageQueuesModal = React.memo(
  ({
    queueIds,
    login,
    loginToAllowedQueuesOptionsMap,
    loginByAgentLoginAndQueueIds,
    logoutByAgentLoginAndQueueIds,
    handleRefresh
  }) => {
    const systemConfiguration = useSelector(
      (state) => state.systemConfiguration
    );

    const queuesOptions = useMemo(() => {
      return loginToAllowedQueuesOptionsMap?.[login] ?? [];
    }, [loginToAllowedQueuesOptionsMap, login]);

    const initialSelectedQueues = useMemo(() => {
      return queuesOptions.filter((q) => queueIds.includes(q.value));
    }, [queuesOptions, queueIds]);

    const { handleSubmit, control, errors } = useForm({
      defaultValues: {
        queues: initialSelectedQueues
      }
    });

    const onSubmit = useCallback(
      async (data) => {
        const selectedQueues = data.queues ?? [];
        const newQueueIds = selectedQueues.map((q) => q.value);

        const queuesToLogin = newQueueIds.filter(
          (id) => !queueIds.includes(id)
        );
        const queuesToLogout = queueIds.filter(
          (id) => !newQueueIds.includes(id)
        );

        const operations = [];

        if (queuesToLogin.length) {
          operations.push({
            type: 'login',
            promise: loginByAgentLoginAndQueueIds({ login, queuesToLogin })
          });
        }

        if (queuesToLogout.length) {
          operations.push({
            type: 'logout',
            promise: logoutByAgentLoginAndQueueIds({
              login,
              queuesToLogout
            })
          });
        }

        if (operations.length === 0) {
          return;
        }

        const results = await Promise.allSettled(
          operations.map((op) => op.promise)
        );

        const hasSuccess = results.some(
          (result) => result.status === 'fulfilled'
        );

        const errorMessages = results
          .map((result, index) => ({ result, operation: operations[index] }))
          .filter(({ result }) => result.status === 'rejected')
          .map(({ result, operation }) => {
            const opType = operation.type;
            const actionText =
              opType === 'login'
                ? 'logar o atendente nas novas filas'
                : 'deslogar o atendente das filas removidas';
            console.error(`Falha na operação de ${opType}:`, result.reason);
            return `Falha ao ${actionText}.`;
          });

        if (hasSuccess) {
          toast.success('Filas do atendente atualizadas com sucesso!');
          handleRefresh();
        }

        if (errorMessages.length > 0) {
          toast.error(errorMessages.join(' '));
        }
      },
      [
        login,
        queueIds,
        loginByAgentLoginAndQueueIds,
        logoutByAgentLoginAndQueueIds,
        handleRefresh
      ]
    );

    return (
      <UncontrolledDefaultModal
        trigger={
          <Button
            color="primary"
            size="sm"
            title="Gerenciar filas deste atendente (Logar/Deslogar)"
          >
            <li className="fas fa-list-ul"></li>
          </Button>
        }
        header="Gerenciar Filas"
        title="Gerenciar filas do atendente (Logar/Deslogar)"
        subtitle={`Adicione ou remova as filas para este atendente`}
        size="lg"
        body={
          <Row style={{ paddingInline: '1rem' }}>
            <Col md="12">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Row
                  className="align-items-center no-gutters"
                  style={{ gap: '0.5rem' }}
                >
                  <Col>
                    <FlexControllerV2
                      type="select"
                      name={`queues`}
                      control={control}
                      options={queuesOptions}
                      isMulti
                      closeMenuOnSelect={false}
                      errors={errors}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button color="primary" type="submit">
                      Salvar
                    </Button>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>
        }
        footer={({ closeModal }) => (
          <div
            className="modal-footer"
            style={{ paddingTop: '.5rem', paddingInline: '.75rem' }}
          >
            <Button
              type="button"
              color="danger"
              style={{
                backgroundColor: systemConfiguration.cancelation_button_color
              }}
              onClick={closeModal}
            >
              Fechar
            </Button>
          </div>
        )}
      ></UncontrolledDefaultModal>
    );
  }
);

export default ManageQueuesModal;
